import { type Picture } from '@/components/atoms/AppImage';
import { type AccessoryCategory, type KnownAccessoryCategory } from '@/services/accessories/types';

/**
 * The shelf photographs, encoded for the web.
 *
 * A glob and not a list of named imports, for the same reason `sitePhotos.ts`
 * is one: a named import of a file nobody has supplied yet is a build error,
 * and a shelf whose photograph has not arrived should fall back to its plate
 * rather than stop the site shipping. Dropping a file in
 * `assets/photos/accessories/` is therefore a file copy and no code change, and
 * deleting one cannot break the build.
 *
 * Two widths rather than the three `sitePhotos` uses. A tile is drawn at
 * ~165px on a phone (two across), ~265px at `lg` (four across), and never
 * wider — so 480 covers a 2x phone and 800 covers a 2x desktop tile with room
 * to spare. A 1200 rendition would be downloaded by nobody.
 *
 * `quality=80`, not the 72 the site photographs use. Everything here arrived
 * already compressed — five AVIF, one WebP, one JPEG (`assets/photos/README.md`
 * asks for JPEG originals for exactly this reason) — so the build is
 * re-encoding a lossy file and 72 on top of 72 shows as mush on the flat
 * backgrounds these product shots have. 80 keeps the second generation close
 * to the first at a file size a tile can afford.
 *
 * Nothing is enlarged: imagetools reverts to the source's own dimensions rather
 * than upscale, so a 550px source simply stops at 550 and the `sizes` below
 * picks the best rendition that exists.
 */
const files = import.meta.glob<Picture>('../assets/photos/accessories/*.{jpg,jpeg,png,webp,avif}', {
  eager: true,
  import: 'default',
  // Written out in full rather than held in a constant: Vite reads these
  // options statically, before any code runs, and refuses a value it has to
  // evaluate to know.
  query: '?w=480;800&format=avif;webp&quality=80&as=picture',
});

/**
 * Which file belongs to which shelf.
 *
 * The filenames follow the label a visitor reads ("Exterior & styling",
 * "Service parts") and the category values follow the data model
 * (`exterior`, `maintenance`), and those two vocabularies do not line up — so
 * the join is stated here rather than derived. Keys are compared lowercased,
 * which is why `Interior-and-comfort.avif` needs no special case.
 *
 * A file whose stem is not in this table is ignored rather than guessed at: a
 * photograph silently landing on the wrong shelf is worse than a shelf that
 * keeps its plate, and `pnpm check` cannot catch a bad guess.
 */
const categoryByStem: Readonly<Record<string, KnownAccessoryCategory>> = {
  'car-wheels-and-tyres': 'wheels-tyres',
  'exterior-and-styling': 'exterior',
  'interior-and-comfort': 'interior',
  electronics: 'electronics',
  security: 'protection',
  performance: 'performance',
  'service-parts': 'maintenance',
};

const byCategory: Partial<Record<string, Picture>> = {};

for (const [path, picture] of Object.entries(files)) {
  const stem = path
    .slice(path.lastIndexOf('/') + 1)
    .replace(/\.[^.]+$/, '')
    .toLowerCase();
  const category = categoryByStem[stem];
  if (category) byCategory[category] = picture;
}

/**
 * What the tile is drawn at, so the browser fetches the rendition it will
 * actually use and not the one after it.
 *
 * Read off the grid in `AccessoryCategoryTiles`: two across below `sm`, three
 * from `sm`, four from `lg`, inside a `maxWidth="lg"` container — which puts
 * the widest a tile ever gets at about 265px.
 */
export const ACCESSORY_TILE_SIZES = '(min-width: 1200px) 265px, (min-width: 600px) 33vw, 50vw';

/**
 * The photograph for a shelf, or `undefined` when none has been supplied.
 *
 * Callers hand the result to `MediaFrame`, which draws the photo if there is
 * one and the shelf's `MediaPlate` if there is not. No caller should branch on
 * this itself — a missing photograph is an ordinary state here, not an error,
 * and it is now the EXPECTED state for a shelf an administrator added from
 * Kabex Hub: artwork cannot be typed into a form, so a new shelf wears its
 * plate until someone supplies a file.
 */
export function accessoryCategoryPhoto(category: AccessoryCategory): Picture | undefined {
  return byCategory[category];
}
